import * as RadixCheckbox from "@radix-ui/react-checkbox";
import cn from "clsx";
import { CheckLineThick, SubtractLineThick } from "./Icons";
import { forwardRef } from "react";

export const INDETERMINATE = "indeterminate";
type RadixCheckboxProps = RadixCheckbox.CheckboxProps;

interface BaseProps {
  name?: RadixCheckboxProps["name"];
  disabled?: RadixCheckboxProps["disabled"];
  required?: RadixCheckboxProps["required"];
  size?: "sm" | "md" | "lg";
  id?: RadixCheckboxProps["id"];
}

interface BinaryCheckboxProps extends BaseProps {
  supportsIndeterminate?: false;
  checked?: boolean;
  onCheckedChange?: (v: boolean) => void;
}

interface TertiaryCheckboxProps extends BaseProps {
  supportsIndeterminate: true;
  checked?: RadixCheckboxProps["checked"];
  onCheckedChange?: RadixCheckboxProps["onCheckedChange"];
}

type CheckboxProps = BinaryCheckboxProps | TertiaryCheckboxProps;

/**
 * Checkbox component that wraps Radix UI Checkbox with additional props.
 *
 * This component supports two modes:
 * - Binary mode: When `supportsIndeterminate` is `false` or not provided, the checkbox behaves as a standard binary checkbox.
 * - Tertiary mode: When `supportsIndeterminate` is `true`, the checkbox can be in an indeterminate state.
 */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ id, name, checked, disabled, required, size = "md", supportsIndeterminate, onCheckedChange }, ref) => {
    const value = supportsIndeterminate ? checked : checked === true;

    const IndicatorComponent = getIndicatorComponent(checked);

    return (
      <RadixCheckbox.Root
        id={id}
        data-testid={id}
        ref={ref}
        name={name}
        disabled={disabled}
        checked={value}
        required={required}
        onCheckedChange={onCheckedChange}
        className={cn("rounded-sm ring-1", {
          // Size
          "w-4 h-4": size === "sm",
          "w-5 h-5": size === "md",
          "w-6 h-6": size === "lg",
          // State
          "bg-white ring-blue-grey-200 hover:ring-blue-grey-300 disabled:bg-blue-grey-300 disabled:ring-blue-grey-300":
            value === false,
          "bg-platform-blue-700 ring-platform-blue-700 hover:ring-platform-blue-800 hover:bg-platform-blue-800 disabled:bg-blue-grey-300 disabled:ring-blue-grey-300":
            value === true || value === INDETERMINATE,
          "cursor-not-allowed": disabled,
        })}
      >
        <RadixCheckbox.Indicator className="flex items-center justify-center">
          {IndicatorComponent && (
            <IndicatorComponent
              className={cn("text-white", {
                "w-3": size === "sm",
                "w-4": size === "md",
                "w-5": size === "lg",
              })}
            />
          )}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
    );
  }
);

Checkbox.displayName = "Checkbox";

function getIndicatorComponent(checked?: boolean | "indeterminate") {
  if (checked === true) return CheckLineThick;
  if (checked === INDETERMINATE) return SubtractLineThick;
  return null;
}
